import { auth } from '../../utils/auth';
import { AuthStateLoginStatus } from '../../types/auth';
import { closeMessageModal, openMessageModal } from '../../utils/message';
import { isAuthenticated } from '../../utils/authUtils';
import { messageContents } from '../../constants/message';
import { MessageWrapper } from './PrivateRouteStyle';
import {
  ReactNode,
  useEffect,
  useRef,
  useState
  } from 'react';
import { redirectToHome, redirectToSignUp } from '../../utils/router';
import { RootState, useAppSelector } from '../../redux/store';
import { url } from '../../constants/url';
import { useLocation } from 'react-router-dom';
import { usePageSpacers } from '../../hooks/usePageSpacers';
import { usePermissions } from '../../hooks/usePermissions';
import { UserRoles } from '../../types/user-roles';

interface PrivateRouteProps {
  allowedRoles?: boolean | UserRoles | UserRoles[];
  component: ReactNode;
  /**
   * If true, shows an authentication modal before redirecting to login
   * Used for screens like Credit Application where we want to inform users first
   */
  showAuthModalBeforeRedirect?: boolean;
}

function PrivateRoute({
  allowedRoles,
  component,
  showAuthModalBeforeRedirect = false,
}: PrivateRouteProps) {
  const location = useLocation();
  const { isAllowed } = usePermissions();
  const authState = useAppSelector((state: RootState) => state.auth);
  const { navbarSpacerHeight } = usePageSpacers();

  const [accessState, setAccessState] = useState<'loading' | 'allowed' | 'denied'>('loading');
  const [modalShown, setModalShown] = useState(false);
  const [authModalShown, setAuthModalShown] = useState(false);
  // This ref prevents automatic redirects when modal is shown
  const redirectBlockedRef = useRef(false);

  // Check if current path is a credit-related route
  const isCreditRoute = () => {
    const path = location.pathname;
    const creditApplyPath = url.account.subPath.creditAccount.subPath.applyCreditAccount.path;
    const creditManagePath = url.account.subPath.creditAccount.path;

    return path.includes(creditApplyPath) || path.includes(creditManagePath);
  };

  // Function to check if user has required permissions
  const checkPermissions = () => {
    // Don't process until auth is complete
    if (authState.loginStatus === AuthStateLoginStatus.LOADING) {
      return;
    }
    if (!isAuthenticated(authState.loginStatus)) {
      return;
    }

    let isUserAllowed = false;

    if (allowedRoles === undefined) {
      // If no specific roles required, allow access
      isUserAllowed = true;
    } else if (typeof allowedRoles === 'boolean') {
      // Direct boolean override (used for owner check primarily)
      isUserAllowed = allowedRoles;
    } else {
      // Check if user has the required role(s)
      isUserAllowed = isAllowed({ allowedRoles: allowedRoles });
    }
    setAccessState(isUserAllowed ? 'allowed' : 'denied');

    if (!isUserAllowed && showAuthModalBeforeRedirect && !modalShown) {
      openMessageModal(messageContents.authorizationErrorMessage);
      setModalShown(true);
    }
  };

  // Handle authentication - redirect to login if not authenticated
  useEffect(() => {
    // Skip if we're loading auth state or if we've already shown the modal
    if (authState.loginStatus === AuthStateLoginStatus.LOADING || redirectBlockedRef.current) {
      return;
    }

    // If not authenticated and not in loading state
    if (!isAuthenticated(authState.loginStatus)) {
      if ((showAuthModalBeforeRedirect || isCreditRoute()) && !authModalShown) {
        // Prevent any redirects from happening automatically
        redirectBlockedRef.current = true;
        setAuthModalShown(true);
        openMessageModal({
          title: messageContents.creditAuthenticationMessage.title,
          description: messageContents.creditAuthenticationMessage.description,
          confirmButtonLabel: messageContents.creditAuthenticationMessage.confirmButtonLabel,
          confirmClickHandler: () => {
            redirectBlockedRef.current = false;
            closeMessageModal();
            redirectToSignUp(location.pathname, isCreditRoute());
          },
          closeClickHandler: () => {
            redirectBlockedRef.current = false;
            closeMessageModal();
            redirectToHome();
          },
        });
      } else if (!redirectBlockedRef.current) {
        // Direct login for non-credit routes (without showing modal)
        auth.login(window.location.href, true);
      }
    } else {
      // Call permission check whenever auth state changes
      checkPermissions();
    }
  }, [authState, showAuthModalBeforeRedirect, authModalShown]);

  // Run permission check when dependencies change
  useEffect(() => {
    checkPermissions();
  }, [isAllowed, allowedRoles]);

  // Determine what to render based on current state
  const renderContent = () => {
    if (accessState === 'loading') {
      return <MessageWrapper navbarSpacerHeight={navbarSpacerHeight}>Loading...</MessageWrapper>;
    }

    if (!isAuthenticated(authState.loginStatus) && !redirectBlockedRef.current) {
      return (
        <MessageWrapper navbarSpacerHeight={navbarSpacerHeight}>Redirecting...</MessageWrapper>
      );
    }

    if (accessState === 'denied') {
      return null;
    }

    return <>{component}</>;
  };

  return renderContent();
}

export default PrivateRoute;
